import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";

const DEFAULT_TITLE = "Minutezilla";

function padTime(value) {
  return String(value).padStart(2, "0");
}

function formatElapsedTitle(startedAt, taskTitle) {
  const elapsedMs = Math.max(Date.now() - Number(startedAt || 0), 0);
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const hours = padTime(Math.floor(totalSeconds / 3600));
  const minutes = padTime(Math.floor((totalSeconds % 3600) / 60));
  const seconds = padTime(totalSeconds % 60);
  return `${hours}:${minutes}:${seconds} - ${taskTitle || DEFAULT_TITLE}`;
}

export default function GlobalTimerTitle() {
  const auth = useSelector((state) => state.auth);
  const [runningTimer, setRunningTimer] = useState(null);
  const refreshTimerRef = useRef(null);

  const authHeaders = useMemo(() => {
    const headers = { "Content-Type": "application/json" };
    if (auth?.token) {
      headers.Authorization = `Bearer ${auth.token}`;
    }
    return { headers };
  }, [auth?.token]);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const applyDocumentTitle = useCallback((timerState) => {
    if (timerState?.startedAt) {
      document.title = formatElapsedTitle(timerState.startedAt, timerState.title);
      return;
    }
    document.title = DEFAULT_TITLE;
  }, []);

  const syncRunningTimer = useCallback(async () => {
    if (!auth?.isAuthenticated || !auth?.workspace_id) {
      setRunningTimer(null);
      return;
    }

    try {
      const res = await axios.post(
        "/api/usertime/list",
        JSON.stringify({
          workspace_id: auth.workspace_id,
          offset: 0,
        }),
        authHeaders
      );

      const activeEntry = Array.isArray(res.data?.running) ? res.data.running[0] : null;
      if (activeEntry && Number(activeEntry.isStop) === 0) {
        setRunningTimer({
          startedAt: Number(activeEntry.timerStart || 0),
          title: activeEntry.title || "",
        });
      } else {
        setRunningTimer(null);
      }
    } catch (_error) {
      setRunningTimer(null);
    }
  }, [auth?.isAuthenticated, auth?.workspace_id, authHeaders]);

  useEffect(() => {
    clearRefreshTimer();

    if (!auth?.isAuthenticated || !auth?.workspace_id) {
      setRunningTimer(null);
      applyDocumentTitle(null);
      return undefined;
    }

    syncRunningTimer();

    const handleTimerChanged = () => {
      syncRunningTimer();
    };

    window.addEventListener("minutezilla:timer-changed", handleTimerChanged);

    refreshTimerRef.current = window.setTimeout(function scheduleRefresh() {
      syncRunningTimer().finally(() => {
        refreshTimerRef.current = window.setTimeout(scheduleRefresh, 15000);
      });
    }, 15000);

    return () => {
      window.removeEventListener("minutezilla:timer-changed", handleTimerChanged);
      clearRefreshTimer();
    };
  }, [
    auth?.isAuthenticated,
    auth?.workspace_id,
    applyDocumentTitle,
    clearRefreshTimer,
    syncRunningTimer,
  ]);

  useEffect(() => {
    applyDocumentTitle(runningTimer);

    if (!runningTimer?.startedAt) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      applyDocumentTitle(runningTimer);
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [applyDocumentTitle, runningTimer]);

  return null;
}
